import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Broadcast,
  ChatsCircle,
  MusicNote,
  SealCheck,
  Sparkle,
  Storefront,
  Trophy,
  Users,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { disciplines as mockDisciplines } from "@/data/mockData";

function mediaUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

const QUOTES = [
  {
    by: "Mar Quintero",
    role: "Pintora · Madrid",
    text: "Elarte me dio una web pública en 10 minutos. Mi kit de prensa antes tardaba semanas.",
  },
  {
    by: "Kenji Ríos",
    role: "Artista digital · CDMX",
    text: "Por fin una red social donde el algoritmo no entierra la obra lenta.",
  },
  {
    by: "Lola Serrano",
    role: "Música · Barcelona",
    text: "Subo un EP y queda archivado con BPM, key y créditos. No es Instagram, es un archivo vivo.",
  },
];

const FEATURES = [
  {
    icon: Users,
    title: "Directorio vivo",
    text: "Perfiles verificados manualmente. Sin bots, sin portfolios falsos.",
  },
  {
    icon: ChatsCircle,
    title: "Red social propia",
    text: "Feed, historias, DMs y notificaciones en tiempo real vía WebSockets.",
  },
  {
    icon: Broadcast,
    title: "Live arte & salas 24/7",
    text: "Transmite tu estudio, invita colabs al escenario o entra a una sala permanente.",
  },
  {
    icon: Storefront,
    title: "Marketplace",
    text: "Vende obra, prints, NFT o comisiones. 92% al artista, 8% plataforma.",
  },
  {
    icon: Trophy,
    title: "Convocatorias",
    text: "Becas, residencias y premios curados. Postula sin pagar intermediarios.",
  },
  {
    icon: Sparkle,
    title: "Kit de prensa auto",
    text: "Bio, stats, obra y contactos empaquetados en una URL compartible.",
  },
];

export default function Landing() {
  const { site, disciplines: liveDisc } = useBrand();
  const disciplines = liveDisc && liveDisc.length ? liveDisc : mockDisciplines;
  const [artists, setArtists] = useState([]);
  const [stats, setStats] = useState({ artists: 0, disciplines: disciplines.length, countries: 0 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users", { params: { limit: 12 } });
        const list = Array.isArray(data) ? data : data?.users || data?.items || [];
        setArtists(list.filter((u) => u.role !== "admin").slice(0, 10));
        const countries = new Set(list.map((u) => u.country).filter(Boolean));
        setStats((s) => ({
          ...s,
          artists: list.length,
          countries: countries.size || 12,
        }));
      } catch (_) {
        setArtists([]);
      }
    })();
  }, []);

  const siteName = site?.site_name || "ELARTE/NET";

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-[#050505] text-[#F4F4F5] selection:bg-[#FF3E00] selection:text-white"
    >
      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-lg border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-display text-xl md:text-2xl font-black uppercase tracking-tighter">
            ELARTE<span className="text-[#FF3E00]">/</span>NET
          </Link>
          <nav className="hidden md:flex items-center gap-6 font-mono text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA]">
            <a href="#manifiesto" className="hover:text-white">Manifiesto</a>
            <a href="#disciplinas" className="hover:text-white">Disciplinas</a>
            <a href="#directorio" className="hover:text-white">Directorio</a>
            <a href="#plataforma" className="hover:text-white">Plataforma</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/ingresar"
              data-testid="landing-login"
              className="hidden sm:inline-block font-mono text-[11px] tracking-[0.22em] uppercase text-[#A1A1AA] hover:text-white px-3 py-2"
            >
              Ingresar
            </Link>
            <Link
              to="/registro"
              data-testid="landing-register-top"
              className="btn-brutal font-mono text-[11px] tracking-[0.22em] uppercase font-bold bg-[#FF3E00] text-white px-4 py-2.5"
            >
              Crear perfil
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-[#27272A] overflow-hidden">
        {/* noise texture */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='1'/></svg>\")",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-20 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="font-mono text-[10px] md:text-[11px] text-[#FF3E00] tracking-[0.32em] mb-5">
              // RED + DIRECTORIO DE ARTISTAS · VERIFICACIÓN MANUAL
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter leading-[0.82] text-[54px] sm:text-[84px] lg:text-[128px]">
              EL ARTE<br />
              NO ES <span className="text-[#FF3E00]">CONTENIDO</span>.
            </h1>
            <p className="mt-8 max-w-xl font-mono text-[13px] md:text-[14px] text-[#A1A1AA] tracking-wider leading-relaxed">
              {siteName} es la plataforma para artistas que quieren un lugar
              propio —directorio público, red social, marketplace y kit de
              prensa— sin algoritmos que entierren la obra lenta.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/registro"
                data-testid="landing-register-hero"
                className="btn-brutal inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold bg-[#FF3E00] text-white px-6 py-4"
              >
                Crear mi perfil <ArrowRight size={14} weight="bold" />
              </Link>
              <Link
                to="/explorar"
                data-testid="landing-explore-hero"
                className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] border border-[#27272A] hover:border-[#F4F4F5] text-[#F4F4F5] px-6 py-4"
              >
                Explorar directorio <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="mt-10 inline-flex items-center gap-3 border border-[#27272A] bg-[#0a0a0a] px-4 py-2">
              <SealCheck size={13} weight="fill" className="text-[#00FF66]" />
              <span className="font-mono text-[10px] text-[#00FF66] tracking-[0.22em] uppercase">
                Verificación manual 24-48h
              </span>
              <span className="font-mono text-[10px] text-[#525252] tracking-[0.22em] uppercase">
                · Sin bots · Sin portfolios falsos
              </span>
            </div>
          </div>

          {/* Right — brutalist tag panel */}
          <div className="grid grid-cols-2 gap-3">
            <StatBox k="ARTISTAS" v={stats.artists || "—"} accent="text-[#FF3E00]" />
            <StatBox k="DISCIPLINAS" v={stats.disciplines} />
            <StatBox k="PAÍSES" v={stats.countries || 12} />
            <StatBox k="TIEMPO REAL" v="LIVE" accent="text-[#00FF66]" mono />
            <div className="col-span-2 border border-[#27272A] bg-[#0a0a0a] p-5">
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-3">
                // LO QUE ENCONTRARÁS
              </div>
              <ul className="space-y-2 font-mono text-[12px] text-[#E5E5E5] tracking-wider">
                <li>→ Directorio con 6 disciplinas</li>
                <li>→ Feed social, DMs y notificaciones en vivo</li>
                <li>→ Live, salas 24/7 y colabs en el escenario</li>
                <li>→ Marketplace + convocatorias + kit de prensa</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFIESTO */}
      <section id="manifiesto" className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em]">
            // MANIFIESTO<br/>01 · POR QUÉ
          </div>
          <div className="max-w-3xl">
            <p className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[1.02]">
              Hecho por artistas para artistas.<br/>
              <span className="text-[#525252]">Nada de feeds tóxicos.</span> <span className="text-[#FF3E00]">Territorio propio.</span>
            </p>
            <p className="mt-8 font-mono text-[13px] text-[#A1A1AA] tracking-wider leading-relaxed max-w-2xl">
              Creamos {siteName} porque las plataformas generalistas aplanan la
              obra: tu práctica compite con memes, tu release con reels. Acá
              cada artista tiene una URL pública con bio larga, portafolio,
              música, shop y kit de prensa — y una red social que sí premia la
              obra lenta.
            </p>
          </div>
        </div>
      </section>

      {/* DISCIPLINAS */}
      <section id="disciplinas" className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-3">
                // 02 · DISCIPLINAS
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
                Seis territorios.<br/>Uno <span className="text-[#FF3E00]">cualquiera</span> puede ser el tuyo.
              </h2>
            </div>
            <Link
              to="/disciplinas"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white inline-flex items-center gap-2"
            >
              Ver todas <ArrowUpRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {disciplines.map((d, i) => (
              <Link
                to="/explorar"
                key={d.slug}
                data-testid={`landing-discipline-${d.slug}`}
                className="group relative border border-[#27272A] bg-[#0a0a0a] hover:bg-[#FF3E00] hover:border-[#FF3E00] p-6 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-[#525252] group-hover:text-white/70 tracking-[0.22em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="text-[#525252] group-hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="mt-6 font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-[0.95] group-hover:text-white">
                  {d.name}
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] text-[#A1A1AA] group-hover:text-white/70">
                  {d.tag} {d.count ? `· ${d.count}` : ""}
                </div>
                {d.sub_categories?.length ? (
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {d.sub_categories.slice(0, 4).map((sc) => (
                      <span
                        key={sc.slug}
                        className="font-mono text-[9px] uppercase tracking-[0.2em] border border-[#27272A] group-hover:border-white/30 px-1.5 py-0.5 text-[#A1A1AA] group-hover:text-white/80"
                      >
                        {sc.name}
                      </span>
                    ))}
                  </div>
                ) : null}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DIRECTORIO */}
      <section id="directorio" className="border-b border-[#27272A] bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-3">
                // 03 · DIRECTORIO EN VIVO
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
                Artistas <span className="text-[#FF3E00]">reales</span>,<br/>
                perfiles verificados.
              </h2>
            </div>
            <Link
              to="/explorar"
              data-testid="landing-go-directory"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#FF3E00] hover:text-white inline-flex items-center gap-2"
            >
              Ir al directorio completo <ArrowUpRight size={12} />
            </Link>
          </div>

          {artists.length === 0 ? (
            <div className="border border-dashed border-[#27272A] py-16 text-center font-mono text-[10px] text-[#525252] uppercase tracking-[0.22em]">
              · cargando directorio ·
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {artists.map((a) => (
                <Link
                  to={`/artista/${a.handle}`}
                  key={a.id}
                  data-testid={`landing-artist-${a.handle}`}
                  className="group border border-[#27272A] bg-[#050505] hover:border-[#FF3E00] transition-colors"
                >
                  <div className="relative aspect-[4/5] bg-[#121212] overflow-hidden">
                    {a.photo ? (
                      <img
                        src={mediaUrl(a.photo)}
                        alt={a.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter]"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center font-display text-3xl font-black text-[#27272A]">
                        {(a.name || a.handle || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    {a.type === "musician" || a.music_release ? (
                      <div className="absolute top-2 left-2 bg-[#FF3E00] text-white px-1.5 py-0.5 font-mono text-[9px] tracking-[0.22em] inline-flex items-center gap-1">
                        <MusicNote size={9} weight="fill" /> MÚSICA
                      </div>
                    ) : null}
                    {a.verified ? (
                      <div className="absolute top-2 right-2 bg-black/70 text-[#00FF66] px-1 py-0.5">
                        <SealCheck size={11} weight="fill" />
                      </div>
                    ) : null}
                  </div>
                  <div className="p-3">
                    <div className="font-display font-black uppercase tracking-tight text-sm truncate group-hover:text-[#FF3E00]">
                      {a.name || a.handle}
                    </div>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.2em] truncate">
                      @{a.handle}
                    </div>
                    <div className="font-mono text-[10px] text-[#525252] tracking-[0.2em] truncate mt-0.5">
                      {a.city || "—"}
                      {a.country ? ` · ${a.country}` : ""}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURES */}
      <section id="plataforma" className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="mb-12">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-3">
              // 04 · TODO EN UN LUGAR
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
              Una plataforma.<br/>
              <span className="text-[#525252]">Seis herramientas.</span> <span className="text-[#FF3E00]">Una carrera.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`feature-${i}`}
                  className="bg-[#050505] p-6 md:p-8 hover:bg-[#0a0a0a] transition-colors"
                >
                  <div className="flex items-center justify-between mb-6">
                    <Icon size={28} weight="duotone" className="text-[#FF3E00]" />
                    <span className="font-mono text-[10px] text-[#525252] tracking-[0.22em]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight leading-[1.05]">
                    {f.title}
                  </div>
                  <p className="mt-3 font-mono text-[12px] text-[#A1A1AA] tracking-wider leading-relaxed">
                    {f.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUOTES */}
      <section className="border-b border-[#27272A] bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-10">
            // 05 · LO QUE DICEN
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {QUOTES.map((q, i) => (
              <figure
                key={i}
                className="border border-[#27272A] bg-[#050505] p-6 md:p-8 relative"
              >
                <span
                  aria-hidden
                  className="absolute -top-4 left-6 font-display text-[72px] text-[#FF3E00] leading-none"
                >
                  “
                </span>
                <blockquote className="font-display text-lg md:text-xl italic text-[#E5E5E5] leading-snug">
                  {q.text}
                </blockquote>
                <figcaption className="mt-6 pt-4 border-t border-[#27272A]">
                  <div className="font-display font-black uppercase tracking-tight">
                    {q.by}
                  </div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                    {q.role}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="border-b border-[#27272A] relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28 text-center relative">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-5">
            // 06 · EMPIEZA
          </div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-[54px] sm:text-[96px] lg:text-[144px]">
            RESERVA<br/>
            TU <span className="text-[#FF3E00]">HANDLE</span>.
          </h2>
          <p className="mt-8 max-w-xl mx-auto font-mono text-[12px] md:text-[13px] text-[#A1A1AA] tracking-wider">
            ELARTE.NET/ARTISTA/@tu_nombre · 10 minutos · gratis mientras dure
            el registro abierto.
          </p>
          <div className="mt-10 flex items-center justify-center flex-wrap gap-3">
            <Link
              to="/registro"
              data-testid="landing-register-bottom"
              className="btn-brutal inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold bg-[#FF3E00] text-white px-8 py-4"
            >
              Crear mi perfil <ArrowRight size={14} weight="bold" />
            </Link>
            <Link
              to="/ingresar"
              className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] border border-[#27272A] hover:border-[#F4F4F5] text-[#F4F4F5] px-8 py-4"
            >
              Ya tengo cuenta
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050505]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="col-span-2 md:col-span-1">
            <div className="font-display text-xl font-black uppercase tracking-tighter">
              ELARTE<span className="text-[#FF3E00]">/</span>NET
            </div>
            <p className="mt-3 font-mono text-[10px] text-[#525252] tracking-[0.22em] uppercase max-w-[220px]">
              Red + directorio de artistas verificados · {new Date().getFullYear()}
            </p>
          </div>
          <FooterCol
            title="Plataforma"
            links={[
              ["Directorio", "/explorar"],
              ["Disciplinas", "/disciplinas"],
              ["Convocatorias", "/convocatorias"],
              ["Marketplace", "/tienda"],
            ]}
          />
          <FooterCol
            title="Comunidad"
            links={[
              ["Colaboraciones", "/colaboraciones"],
              ["Live arte", "/live"],
              ["Salas 24/7", "/salas"],
              ["Boletín", "/boletin"],
            ]}
          />
          <FooterCol
            title="Cuenta"
            links={[
              ["Crear perfil", "/registro"],
              ["Ingresar", "/ingresar"],
              ["Ajustes", "/ajustes"],
            ]}
          />
        </div>
        <div className="border-t border-[#27272A]">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between flex-wrap gap-2 font-mono text-[10px] text-[#525252] tracking-[0.22em] uppercase">
            <span>© {new Date().getFullYear()} {siteName}</span>
            <span>Brutalist · Built for artists</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatBox({ k, v, accent = "text-[#F4F4F5]", mono = false }) {
  return (
    <div className="border border-[#27272A] bg-[#0a0a0a] p-5">
      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
        {k}
      </div>
      <div
        className={`mt-3 font-display font-black tracking-tighter leading-none ${accent} ${
          mono ? "text-3xl" : "text-5xl md:text-6xl"
        }`}
      >
        {v}
      </div>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] uppercase mb-3">
        {title}
      </div>
      <ul className="space-y-2">
        {links.map(([label, to]) => (
          <li key={to}>
            <Link
              to={to}
              className="font-mono text-[11px] text-[#A1A1AA] hover:text-white tracking-[0.22em] uppercase"
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
